import React from 'react';
import classNames from 'classnames';

import Card from '@material-ui/core/Card';
import createStyles from "@material-ui/core/styles/createStyles";
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import TablePagination from '@material-ui/core/TablePagination';
import Typography from '@material-ui/core/Typography';
import { Theme, withStyles } from '@material-ui/core/styles';

//@ts-ignore
import IndeterminateLoadingLinear from '../IndeterminateLoadingLinear';

const styles = (theme: Theme) => createStyles({
  root: {
    maxWidth: '1280px',
    margin: "0 auto"
  },
  title: {
    '& > div': {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    '& > * > *': {
      padding: `0px ${theme.spacing(1)}px`,
    },
  },
  message: {
    padding: theme.spacing(1) * 4,
    textAlign: 'center',
    color: '#888',
  },
  list: {
    paddingTop: 'unset',
    paddingBottom: 'unset',
    marginTop: theme.spacing(1),
  },
  pagination: {
    margin: 0,
    padding: 0
  },
  paginationToolbar: {
    padding: 0
  }
});

interface Props {
  title?: string | null;
  header?: any;
  classes?: any;
  items: any;
  defaultText?: string | null;
  isLoading: boolean;
  cardStyle?: any;
  rootClassOverride?: string;
  noCard?: boolean;

  /* Pagination */
  paginationEnabled?: boolean;
  disableDynamicPagination?: boolean;
  itemTotal?: number; // Needed for MUIs Table Pagination Component
  listViewId?: string; // Used when having multiple list views on a page
  updateList?: (limit: number, offset: number) => void; // Used to update shown items
  inDialog?: boolean; // Used for calculating list view offset when inside a dialog

  /* Multi-Selection */
  selectionData?: any; // Used to keep track of multi-selection data
  doUpdateSelectionData?: (state: any) => void; // Used to update the state of selected items
}

interface State {
  page: number;
  rowsPerPage: number;
  rowsPerPageOptions: number[];
  windowHeight: number;
}

//update the init paginations to 50
const initRowsPerPageOptions = [5, 10, 15, 20,50];

class ListView extends React.PureComponent<Props, State> {
  localPagination = JSON.parse(localStorage.getItem("pagination"));
  isAutoPagination = !this.localPagination || (this.localPagination && this.localPagination.auto && !this.localPagination.rowsPerPage);

  constructor(props: Props) {
    super(props);
    this.state = {
      page: 0,
      rowsPerPage: !this.isAutoPagination ? this.localPagination.rowsPerPage : 10,
      rowsPerPageOptions: [...initRowsPerPageOptions],
      windowHeight: window.innerHeight,
    };
    window.addEventListener("resize", this.resize);
  }

  public static defaultProps: Props = {
    isLoading: false,
    items: undefined,
    noCard: false,
    defaultText: 'No items to display.',
    listViewId: 'listView'
  };

  resize = () => {
    this.setState({ windowHeight: this.getHeight() }, () => this.getDynamicRowsPerPage());
  };

  getHeight = () => {
    const { inDialog, listViewId } = this.props;
    let height;
    if (inDialog) {
      //@ts-ignore
      height = document.getElementById(listViewId) && document.getElementById(listViewId).offsetParent.offsetHeight;
    } else {
      height = window.innerHeight;
    }
    return height;
  };

  getListItems = () => {
    const { classes, cardStyle, defaultText, isLoading, items, listViewId, noCard } = this.props;

    if (isLoading) {
      return (
        <Card id={listViewId} className={classNames(classes.card, cardStyle !== null ? cardStyle : null)}>
          <IndeterminateLoadingLinear />
        </Card>
      );
    }
    if (items === null || items === undefined || items === [] || items.length < 1) {
      return (
        <Typography className={classes.message} variant="h6">
          {defaultText}
        </Typography>
      );
    }

    const flatItems = [...items].flat();
    const listItems = <List id={listViewId} component="ul" className={classes.list}>{flatItems}</List>;

    if (noCard) {
      return listItems;
    }

    return (
      <Card id={listViewId} className={classNames(classes.card, cardStyle !== null ? cardStyle : null)}>
        {listItems}
      </Card>
    );
  };

  updateList = () => {
    const { page, rowsPerPage } = this.state;
    const { updateList } = this.props;
    const limit = rowsPerPage;
    const offset = page * rowsPerPage;
    updateList(limit, offset);
  };

  getPagination = () => {
    const { classes, items, inDialog, itemTotal, paginationEnabled } = this.props;
    const { page, rowsPerPage, rowsPerPageOptions } = this.state;
    return paginationEnabled && items.length > 0 ? (
      <TablePagination
        id={inDialog ? "inDialogPagination" : "pagination"}
        classes={{ root: classes.pagination, toolbar: classes.paginationToolbar }}
        //@ts-ignore
        component="ul"
        count={itemTotal}
        rowsPerPageOptions={[...rowsPerPageOptions]}
        page={page}
        labelRowsPerPage="Rows:"
        rowsPerPage={rowsPerPage}
        onChangePage={this.handleChangePage}
        onChangeRowsPerPage={this.handleChangeRowsPerPage}
      />
    ) : null;
  };

  handleChangePage = (event: any, newPage: number) => {
    this.setState({page: newPage});
  };

  handleChangeRowsPerPage = (event: any) => {
    const rowsPerPage = parseInt(event.target.value, 10);
    let data = {};
    if (!initRowsPerPageOptions.includes(rowsPerPage)) {
      data = { auto: true, rowsPerPage: null };
    } else {
      data = { auto: false, rowsPerPage };
    }
    this.setState({
      rowsPerPage,
      page: 0
    }, () => this.setLocalPagination(data));
  };

  getTitle = () => {
    const { title, classes } = this.props;
    return title ? (
      <div className={classes.title}>
        {title}
        <Divider />
      </div>
    ) : null;
  };

  getHeader = () => {
    const { header }= this.props;
    return header ? (
      <div>
        {header}
        <Divider />
      </div>
    ) : null;
  };

  /**
   * Called when user selects an entity in parent
   * @if shift key is pressed, call `this.handleChangeWithShiftKey(id)`
   * @else call `doUpdateSelectionData(state)` prop
   */
  handleChange = (e: any, id: number) => {
    const { selectionData: { isSelected }, doUpdateSelectionData } = this.props;
    const state = {
      isSelected: isSelected.map((x: any) => x.id === id ? { ...x, status: !x.status } : x),
      lastSelected: isSelected.find((x: any) => x.id === id)
    };
    e.persist();
    e.shiftKey ? this.handleChangeWithShiftKey(id) : doUpdateSelectionData(state);
  };

  /**
   * Called when user selects an entity in parent with shift key
   * @startIndex - start of section that we want to update
   * @endIndex - end of section that we want to update
   * @newSelectionState - whole section we want to update
   * @difference - used to tell direction of selection, if negative (going up) else (going down)
   */
  handleChangeWithShiftKey = (id: number) => {
    const { selectionData: { isSelected, lastSelected }, doUpdateSelectionData } = this.props;
    const count = isSelected.filter((x: any) => x.status === true).length;
    let state = {};

    let startIndex = isSelected.findIndex((x: any) => lastSelected ? x.id === lastSelected.id : isSelected[0] === x);
    if (isSelected[startIndex].status === false && count !== 0) {
      startIndex = isSelected.length - isSelected.slice().reverse().findIndex((x: any) => x.status === true) - 1; // get last selection from list
    }

    const endIndex = isSelected.findIndex((x: any) => x.id === id);
    let newSelectionState = isSelected.slice(startIndex, endIndex + 1);
    const difference = endIndex - startIndex;

    if (difference < 0) {
      // selecting items above
      if (count >= 1){
        newSelectionState = isSelected.slice(endIndex, startIndex);
        if(!newSelectionState.some((x: any) => x.status === false)) {
          newSelectionState = isSelected.slice(endIndex, startIndex + 1);
        }
      } else {
        newSelectionState = isSelected.slice(endIndex, startIndex + 1);
      }

      state = {
        isSelected: isSelected.map((x: any) => newSelectionState.includes(x) ? { ...x, status: !x.status } : x),
        lastSelected: isSelected.find((x: any) => x.id === id)
      };

    } else {
      // selecting items below
      state = {
        isSelected: isSelected.map((x: any) => newSelectionState.includes(x) ? { ...x, status: true } : x),
        lastSelected: isSelected.find((x: any) => x.id === id)
      };
    }
    doUpdateSelectionData(state);
  };

  selectAll = (toSelect: boolean) => {
    const { selectionData: { isSelected }, doUpdateSelectionData } = this.props;
    const state = {
      isSelected: isSelected.map((x: any) => x.status !== toSelect ? {...x, status: toSelect} : x),
      lastSelected: !toSelect ? null : isSelected.pop(),
    };
    doUpdateSelectionData(state);
  };

  invertSelect = () => {
    const { selectionData: { isSelected }, doUpdateSelectionData } = this.props;
    const isSelectedCopy = [...isSelected];
    isSelectedCopy.map((x) => { x.status = !x.status; });

    const state = {
      isSelected: isSelectedCopy,
      lastSelected: isSelectedCopy.some(x => x.status === true) ? // if there are some selected items
        isSelected.slice().reverse().find((x: any) => x.status === true) : // set to last selected in list
        null,
    };
    doUpdateSelectionData(state);
  };

  getDynamicRowsPerPage = () => {
    const { items, inDialog, disableDynamicPagination, listViewId } = this.props;
    const { windowHeight, rowsPerPage } = this.state;
    if (!disableDynamicPagination && items.length > 1) {
      // Calculate Dynamic rows per page
      const listViewOffset = document.getElementById(listViewId) ? document.getElementById(listViewId).offsetTop : 0;
      const paginationID = inDialog ? document.getElementById("inDialogPagination") : document.getElementById("pagination");
      const paginationHeight = paginationID && paginationID.offsetHeight;
      const listViewHeight = windowHeight - listViewOffset - paginationHeight;
      const nodeList = document.getElementById(listViewId) && document.getElementById(listViewId).getElementsByTagName("li");
      //@ts-ignore
      const listItems = nodeList && [...nodeList].map(x => x.offsetHeight);
      const listItemHeight = listItems ? Math.max(...listItems) : null;
      const dynamicRowNumber = listViewHeight && listItemHeight ? Math.floor((listViewHeight / listItemHeight)) : null;

      // Set Dynamic rows per page
      const newRowOptions = [...initRowsPerPageOptions, dynamicRowNumber];
      this.setState(prevState => ({
        rowsPerPage: this.isAutoPagination ? dynamicRowNumber : prevState.rowsPerPage,
        rowsPerPageOptions: [...new Set(newRowOptions)].sort((a, b) => a-b)
      }), () => {
        this.setLocalPagination({
          auto: !!this.isAutoPagination,
          rowsPerPage: this.isAutoPagination ? null : rowsPerPage
        });
      }
      );
    }
  };

  componentWillUnmount() {
    window.removeEventListener("resize", this.resize);
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    const { page, rowsPerPage } = this.state;
    const { items, paginationEnabled } = this.props;
    if (paginationEnabled) {
      this.localPagination = JSON.parse(localStorage.getItem("pagination"));
      this.isAutoPagination = !this.localPagination || (this.localPagination && this.localPagination.auto && !this.localPagination.rowsPerPage);

      // Once items are loaded initialize functions to get dynamic rows
      if (prevProps.isLoading && items && items.length) {
        this.resize();
      }

      // If user changes page or rows per page, update set of items
      if (prevState.page !== page || prevState.rowsPerPage !== rowsPerPage) {
        this.updateList();
      }
    }
  }

  setLocalPagination = (data: any) => {
    localStorage.setItem("pagination", JSON.stringify(data));
  };

  render () {
    const { classes, rootClassOverride } = this.props;
    return (
      <div
        className={classNames(classes.root, rootClassOverride !== null ? rootClassOverride : null)}
      >
        {this.getTitle()}
        {this.getHeader()}
        {this.getListItems()}
        {this.getPagination()}
      </div>
    );
  }
}

export default withStyles(styles)(ListView);
